/* global location */
import React, { Component } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { connect } from "react-redux";
import SchemaForm from "./SchemaForm";
import FormUtils from "../lib/FormUtils";
import utils from "../lib/utils";
import TypeUtils from "../lib/TypeUtils";
import t from "tcomb-form";
import { save, remove } from "../actions/types";
import { push } from "../history";
import { getDecorated } from "../lib/magnets";

import InfoPopover from "./InfoPopover";
import CheckboxGroup from "./fields/CheckboxGroup";
import PartDefinitionList from "./fields/PartDefinitionList";
import RolePermissions from "./fields/RolePermissions";
import Loading from "./Loading";

@connect((state, props) => {
  const { match, location } = props;
  const stateKey = utils.sectionKey(location.pathname);
  const types = state[stateKey];

  return {
    stateKey,
    model: types.find((type) => type.slug === match.params.type) || {},
  };
})
export default class EditType extends Component {
  static contextTypes = {
    site: PropTypes.object.isRequired,
  };

  isAdmin() {
    return this.context.site.permissions.admin === true;
  }

  getSchema = (value) => {
    const parts = value.parts || [];

    let schema = {
      name: {
        type: "string",
        required: true,
        template: (locals) => {
          return (
            <div className="edit-type-name">
              <input
                type="text"
                value={locals.value}
                placeholder="Untitled"
                onChange={(e) => locals.onChange(e.target.value)}
              />
            </div>
          );
        },
      },
      parts: {
        template: (locals) => {
          return <PartDefinitionList locals={locals} />;
        },
        type: "tuple",
        defaultItemValue: {
          label: "Untitled",
          type: "text",
          required: false,
          display_order: parts.length,
        },
        fields: parts.map((partDefinition) => {
          return {
            type: "structure",
            legend: "",
            fields: FormUtils.getPartDefinitionField(partDefinition),
          };
        }),
      },
      options: {
        legend: "",
        type: "structure",
        fields: getDecorated("typeOptions", this.getFormOptions)(value),
      },
    };

    const editable = this.isAdmin() && !value.reserved;
    if (this.props.stateKey === "roles" && !value.reserved) {
      schema = {
        ...schema,
        admin_access: {
          type: "boolean",
          label: "Access to panel?",
          disabled: !editable,
        },
        permissions: {
          type: "dynamic",
          label: "Permissions",
          component: (locals) => {
            return (
              <RolePermissions
                value={locals.value}
                onChange={locals.onChange}
                editable={editable}
              />
            );
          },
        },
      };
    }

    return schema;
  };

  getFormOptions = (value) => {
    const parts = value.parts || [];

    if (!value.collection) {
      return {
        display_columns: {
          label: "Content Columns",
          type: "string",
          allowedValues: [1, 2, 3, 4],
        },
        api_access: {
          label: "Allow API Access?",
          value: false,
          type: "boolean",
        },
      };
    }

    return {
      sort_method: {
        label: "Sort Method",
        value: "date_created",
        type: "string",
        allowedValues: {
          date_created: "Date",
          manual: "Manual",
          hierarchy: "Hierarchy",
          ...parts
            .filter((part) => part.type === "date" || part.type === "text")
            .reduce(
              (prevParts, part) => ({
                ...prevParts,
                [part.name]: part.label,
              }),
              {}
            ),
        },
      },
      sort_order: {
        label: "Sort Order",
        value: "desc",
        type: "string",
        allowedValues: { asc: "Ascending", desc: "Descending" },
      },
      layout: {
        label: "Display Layout",
        value: "default",
        type: "string",
        allowedValues: {
          default: "Default",
          card: "Card",
          table: "Table",
          tree: "Tree",
          // calendar: 'Calendar'
        },
      },
      display_fields: {
        label: "Display Fields",
        type: "list",
        of: {
          type: "string",
          allowedValues: []
            .concat(parts, { label: "Date Created", name: "date_created" })
            .reduce(
              (obj, part) => {
                if (!part.name) return obj;
                obj[part.name] = part.label;
                return obj;
              },
              TypeUtils.isUserRole(value) ? { email: "Email" } : {}
            ),
        },
        factory: t.form.Select,
        component: (locals) => {
          return (
            <CheckboxGroup
              options={locals.options}
              onChange={locals.onChange}
              value={locals.value}
            />
          );
        },
      },
      display_columns: {
        label: "Display Columns",
        type: "string",
        allowedValues: [1, 2, 3],
      },
      api_access: {
        label: "Allow API Access?",
        value: false,
        type: "boolean",
      },
      api_create: {
        label: "Allow public creation via API?",
        value: false,
        type: "boolean",
        condition: { api_access: true },
      },
      notify_on_create: {
        label: "Notify on creation?",
        value: false,
        type: "boolean",
        condition: { api_create: true },
      },
      notify_email: {
        label: "",
        type: "list",
        of: { type: "string" },
        condition: { notify_on_create: true },
      },
    };
  };

  onSubmit = async (value) => {
    if (!value) return;

    const { model, stateKey, dispatch } = this.props;
    const sameName = model.name === value.name;

    let redirect = false;
    if (!model.id) {
      redirect = location.pathname.split("/").slice(0, -1).join("/");
    }
    if (model.id && !sameName) {
      redirect = location.pathname.split("/").slice(0, -2).join("/");
    }

    const dispatchType = stateKey === "types" ? "type" : "role";
    const { error, data: res } = await dispatch(
      save(value, model.id, dispatchType)
    );

    if (error) {
      dispatch(alert({ message: error, type: "error" }));
    } else {
      if (redirect) push(redirect);
    }

    return res[dispatchType];
  };

  onChange = (value, path, kind) => (prevState) => {
    if (path.slice(-1).shift() === "label") {
      const [, partIndex] = path;
      const label = utils.slugify(value.parts[partIndex].label);
      const previous = utils.slugify(prevState.value.parts[partIndex].label);
      if (
        !value.parts[partIndex].name ||
        value.parts[partIndex].name === previous
      ) {
        value = t.update(value, {
          parts: {
            [partIndex]: {
              name: { $set: label },
            },
          },
        });
      }
    }

    return { value };
  };

  onDelete = async (e) => {
    e.preventDefault();
    const { model, stateKey, dispatch } = this.props;
    await dispatch(remove(model, stateKey === "types" ? "type" : "role"));
    push(utils.sectionUrl());
  };

  onDone = (e) => {
    e.preventDefault();
    const { model } = this.props;
    const url = model.id ? utils.contentUrl() : utils.sectionUrl();
    push(url);
  };

  render() {
    const { model } = this.props;

    if (!model) {
      return <Loading type="container" />;
    }

    const inContent = !!this.props.match.params.type;
    const components = [];

    if (model.id) {
      components.push(<InfoPopover key="info" model={model} />);

      const canDelete = TypeUtils.can(this.context.site, model, "delete");
      if (canDelete) {
        components.push(
          <a
            key="delete"
            className="btn-icon ion-ios-trash-outline btn-delete"
            onClick={this.onDelete}
          />
        );
      }
    }

    return (
      <div
        className={cn({ content: !inContent }, "edit-type", "scroll-container")}
      >
        <SchemaForm
          schema={this.getSchema}
          value={model}
          onChange={this.onChange}
          onSubmit={this.onSubmit}
          onCancel={this.onDone}
          actionBar={(props) => components}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/EditType.js